import { z } from 'zod';
import {
  brandingSchema,
  DEFAULT_BRANDING,
  DEFAULT_LOGIN_PAGE,
  loginPageSchema,
} from './branding.js';
import { DEFAULT_DASHBOARD_LAYOUT } from './defaults.js';
import {
  DEFAULT_SERVER_CONSOLE_LAYOUT,
  DEFAULT_SERVER_OVERVIEW_LAYOUT,
} from './defaults-server.js';
import { layoutSchema } from './layout.js';
import { DEFAULT_THEME, themeSchema } from './theme.js';

export const SITE_VERSION = 1;
export const MAX_CUSTOM_CSS = 50_000;

/** Custom CSS the API accepts: size-capped, and nothing that reaches outside the page. */
export const customCssSchema = z
  .string()
  .max(MAX_CUSTOM_CSS, `Custom CSS is limited to ${MAX_CUSTOM_CSS / 1000} KB.`)
  .refine((css) => !/@import\b/i.test(css), '@import isn’t allowed in custom CSS.')
  .refine((css) => !/expression\s*\(/i.test(css), 'expression() isn’t allowed.')
  .refine((css) => !/behavior\s*:/i.test(css), 'behavior: isn’t allowed.')
  .refine((css) => !/-moz-binding/i.test(css), '-moz-binding isn’t allowed.')
  .refine(
    (css) =>
      !/url\(\s*['"]?\s*(?!data:image\/|\/api\/customisation\/assets\/)[a-z][a-z0-9+.-]*:/i.test(
        css,
      ),
    'url() may only reference uploaded assets (/api/customisation/assets/…) or data:image URIs.',
  )
  .default('');

/** Everything about one tenant's panel except its pages. */
export const siteCustomisationSchema = z.object({
  version: z.literal(SITE_VERSION),
  branding: brandingSchema.default(DEFAULT_BRANDING),
  theme: themeSchema.default(DEFAULT_THEME),
  login: loginPageSchema.default(DEFAULT_LOGIN_PAGE),
  dashboard: layoutSchema.default(DEFAULT_DASHBOARD_LAYOUT),
  /** Every server's overview page (inside the server's frame: hero, power and tabs stay fixed). */
  serverOverview: layoutSchema.default(DEFAULT_SERVER_OVERVIEW_LAYOUT),
  /** Every server's console page; the console itself can be moved but not removed. */
  serverConsole: layoutSchema.default(DEFAULT_SERVER_CONSOLE_LAYOUT),
  customCss: customCssSchema,
});
export type SiteCustomisation = z.infer<typeof siteCustomisationSchema>;

export const DEFAULT_SITE: SiteCustomisation = siteCustomisationSchema.parse({ version: 1 });

export function defaultSite(): SiteCustomisation {
  return JSON.parse(JSON.stringify(DEFAULT_SITE)) as SiteCustomisation;
}

/** The site document's layout fields and the surface each is validated for. */
export const SITE_LAYOUTS = [
  { key: 'dashboard', surface: 'dashboard' },
  { key: 'serverOverview', surface: 'server-overview' },
  { key: 'serverConsole', surface: 'server-console' },
] as const;
export type SiteLayoutKey = (typeof SITE_LAYOUTS)[number]['key'];
